import { motion, AnimatePresence } from 'framer-motion';
import { PadlockIcon, TreasureChestIcon, TimerIcon } from './EscapeIcons';
import { Users, X } from 'lucide-react';
import { openBookingModal } from './BookingModal';
import { useLanguage } from '../context/LanguageContext';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BANK_HEIST_IMG = 'https://static.prod-images.emergentagent.com/jobs/f6c7419d-e811-4895-a566-71634139c215/images/c39119d8b1c506688c507fa8eb7bd4e0be42b388477cf48fb18fa85ce2216403.png';
const PIRATE_SHIP_IMG = 'https://static.prod-images.emergentagent.com/jobs/f6c7419d-e811-4895-a566-71634139c215/images/8e9733b15c8862a1e5f3e3052a029a7b7d4d270cbb299296221ab90971599e18.png';
const DEMOLITION_IMG = 'https://images.unsplash.com/photo-1678589717155-487a6452b482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJhbmRvbmVkJTIwY29uc3RydWN0aW9uJTIwc2l0ZSUyMGRlbW9saXRpb24lMjBkYW5nZXJ8ZW58MHx8fHwxNzc2MzQ1Mjc2fDA&ixlib=rb-4.1.0&q=85';
const SWEET_ESCAPE_IMG = 'https://static.prod-images.emergentagent.com/jobs/8b8f8f68-bf24-446b-b0b2-a902344de696/images/0a2383964b1069a889125a7b5d6dc9a44e8a2f7c9f17090cc058cfed93483c8c.png';

export default function EscapeRooms() {
  const { t } = useLanguage();
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomSettings, setRoomSettings] = useState({ oceans13: true, demolition: true, pirate: true, sweet_escape: false });

  useEffect(() => {
    axios.get(`${API}/api/settings/rooms`)
      .then(res => setRoomSettings(res.data))
      .catch(() => {});
  }, []);

  const allRooms = [
    {
      key: 'oceans13',
      title: t('oceans13Title'),
      subtitle: t('bankHeist'),
      description: t('oceans13Desc'),
      image: BANK_HEIST_IMG,
      capacity: `${t('upTo')} 15`,
      price: `${t('from')} ¥4,500`,
      location: 'Rycom Mall',
    },
    {
      key: 'demolition',
      title: t('demolitionTitle'),
      subtitle: t('survivalEscape'),
      description: t('demolitionDesc'),
      image: DEMOLITION_IMG,
      capacity: `${t('upTo')} 4`,
      price: `${t('from')} ¥4,500`,
      location: 'Rycom Mall',
    },
    {
      key: 'pirate',
      title: t('pirateTitle'),
      subtitle: t('treasureHunt'),
      description: t('pirateDesc'),
      image: PIRATE_SHIP_IMG,
      capacity: `${t('upTo')} 8`,
      price: `${t('from')} ¥4,500`,
      location: 'Okinawa City Gate 2',
    },
    {
      key: 'sweet_escape',
      title: t('sweetEscapeTitle'),
      subtitle: t('sweetEscape'),
      description: t('sweetEscapeDesc'),
      image: SWEET_ESCAPE_IMG,
      capacity: '2–6',
      price: `${t('from')} ¥4,500`,
      location: 'Rycom Mall',
      comingSoon: true,
    },
  ];

  const rooms = allRooms.filter(r => roomSettings[r.key]);

  return (
    <section id="rooms" data-testid="rooms-section" className="py-4 sm:py-8 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-4 sm:mb-6">
          <h2 className="font-heading text-2xl sm:text-5xl lg:text-6xl tracking-tight uppercase text-foreground">
            {t('ourEscapeRooms')} <span className="text-brand">{t('escapeRoomsLabel')}</span>
          </h2>
          <p className="font-sans text-sm sm:text-lg text-muted-foreground mt-2 sm:mt-3 max-w-xl mx-auto hidden sm:block">{t('chooseAdventure')}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-6 lg:gap-8">
          {rooms.map((room, i) => (
            <motion.div
              key={room.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.5 }}
              data-testid={`room-card-${i}`}
              className={`${rooms.length === 4 ? 'md:col-span-6' : (i < 2 ? 'md:col-span-6' : 'md:col-span-6 md:col-start-4')} bg-card border border-border rounded-3xl overflow-hidden hover:border-brand/50 transition-all duration-300 group relative`}
            >
              {/* Coming Soon diagonal ribbon */}
              {room.comingSoon && (
                <div className="absolute inset-0 z-20 overflow-hidden pointer-events-none" data-testid={`coming-soon-badge-${i}`}>
                  <div
                    className="absolute top-[50%] left-[50%] w-[150%] text-center -translate-x-1/2 -translate-y-1/2 -rotate-[35deg] font-heading text-2xl sm:text-4xl tracking-[0.3em] uppercase py-3 sm:py-5"
                    style={{
                      background: 'rgba(227, 37, 16, 0.9)',
                      color: '#fff',
                      boxShadow: '0 4px 24px rgba(227,37,16,0.6), 0 0 60px rgba(227,37,16,0.2)',
                      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    }}
                  >
                    {t('comingSoon')}
                  </div>
                </div>
              )}
              {/* Mobile compact layout */}
              <div className="sm:hidden flex items-center gap-3 p-3 cursor-pointer" onClick={() => setSelectedRoom(room)}>
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={room.image} alt={room.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-heading text-[10px] tracking-widest uppercase text-brand">{room.subtitle}</span>
                  <h3 className="font-heading text-lg tracking-tight uppercase text-foreground leading-tight">{room.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-sans text-xs text-muted-foreground">{room.capacity}</span>
                    <span className="text-border">·</span>
                    <span className="font-sans text-xs text-muted-foreground">{room.price}</span>
                    <span className="text-border">·</span>
                    <span className="font-sans text-xs text-muted-foreground">60 {t('min')}</span>
                  </div>
                </div>
              </div>

              {/* Desktop full layout */}
              <div className="hidden sm:block">
                <div className="relative h-72 sm:h-80 overflow-hidden">
                  <img src={room.image} alt={room.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-6">
                    <span className="font-heading text-sm tracking-widest uppercase text-brand">{room.subtitle}</span>
                  </div>
                </div>
                <div className="p-8 sm:p-10">
                  <h3 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-foreground mb-3">{room.title}</h3>
                  <p className="font-sans text-foreground/70 leading-relaxed mb-6">{room.description}</p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <div className="flex items-center gap-2 bg-secondary rounded-full px-4 py-2">
                      <Users size={16} className="text-brand" />
                      <span className="font-sans text-sm font-semibold text-foreground">{room.capacity}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-secondary rounded-full px-4 py-2">
                      <TreasureChestIcon size={16} className="text-brand" />
                      <span className="font-sans text-sm font-semibold text-foreground">{room.price}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-secondary rounded-full px-4 py-2">
                      <TimerIcon size={16} className="text-brand" />
                      <span className="font-sans text-sm font-semibold text-foreground">60 {t('min')}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-secondary rounded-full px-4 py-2">
                      <PadlockIcon size={16} className="text-brand" />
                      <span className="font-sans text-sm text-muted-foreground">{room.location}</span>
                    </div>
                  </div>
                  <button onClick={openBookingModal} data-testid={`room-book-btn-${i}`} className="inline-block font-heading tracking-widest uppercase bg-brand text-white px-8 py-3 rounded-full hover:bg-brand-hover transition-colors neon-glow">
                    {t('bookThisRoom')}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile room detail modal */}
        <AnimatePresence>
          {selectedRoom && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedRoom(null)}
                className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm sm:hidden"
              />
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                className="fixed inset-x-0 bottom-0 z-[10000] sm:hidden max-h-[90vh] overflow-y-auto"
              >
                <div className="bg-card border-t border-border rounded-t-3xl" data-testid="room-detail-modal">
                  {/* Close handle */}
                  <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 rounded-full bg-border" />
                  </div>
                  {/* Room image */}
                  <div className="relative h-48 overflow-hidden mx-4 rounded-2xl">
                    <img src={selectedRoom.image} alt={selectedRoom.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <button
                      onClick={() => setSelectedRoom(null)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white"
                    >
                      <X size={16} />
                    </button>
                    <div className="absolute bottom-3 left-4">
                      <span className="font-heading text-xs tracking-widest uppercase text-brand">{selectedRoom.subtitle}</span>
                    </div>
                  </div>
                  {/* Details */}
                  <div className="p-5">
                    <h3 className="font-heading text-2xl tracking-tight uppercase text-foreground mb-2">{selectedRoom.title}</h3>
                    <p className="font-sans text-sm text-foreground/70 leading-relaxed mb-4">{selectedRoom.description}</p>
                    <div className="flex flex-wrap gap-2 mb-5">
                      <div className="flex items-center gap-1.5 bg-secondary rounded-full px-3 py-1.5">
                        <Users size={14} className="text-brand" />
                        <span className="font-sans text-xs font-semibold text-foreground">{selectedRoom.capacity}</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-secondary rounded-full px-3 py-1.5">
                        <TreasureChestIcon size={14} className="text-brand" />
                        <span className="font-sans text-xs font-semibold text-foreground">{selectedRoom.price}</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-secondary rounded-full px-3 py-1.5">
                        <TimerIcon size={14} className="text-brand" />
                        <span className="font-sans text-xs font-semibold text-foreground">60 {t('min')}</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-secondary rounded-full px-3 py-1.5">
                        <PadlockIcon size={14} className="text-brand" />
                        <span className="font-sans text-xs text-muted-foreground">{selectedRoom.location}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSelectedRoom(null); openBookingModal(); }}
                      data-testid="room-detail-book-btn"
                      className="w-full font-heading tracking-widest uppercase bg-brand text-white py-4 rounded-full hover:bg-brand-hover transition-colors neon-glow text-base"
                    >
                      {t('bookThisRoom')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
